import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

function Stat({ label, value }) {
  return (
    <div className="border border-slate-200 bg-white p-6">
      <p className="eyebrow">{label}</p>
      <p className="font-display text-4xl text-[var(--ink-heading)] mt-3">{value}</p>
    </div>
  );
}

function BreakdownBlock({ title, items }) {
  return (
    <div className="border border-slate-200 bg-white p-6">
      <p className="eyebrow mb-3">{title}</p>
      <ul className="space-y-2 text-sm">
        {Object.entries(items || {}).map(([k, v]) => (
          <li key={k} className="flex items-baseline justify-between border-b border-slate-100 last:border-0 pb-1.5">
            <span className="text-[var(--ink-secondary)] capitalize">{k.replace(/_/g, " ")}</span>
            <span className="font-mono-ui text-xs text-[var(--ink-mono)]">{v}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Portfolio() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.portfolio().then(setData).catch((e) => setErr(e.message));
  }, []);

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="portfolio-page">
      <header className="mb-10">
        <p className="eyebrow">Portfolio</p>
        <h1 className="font-display text-4xl md:text-5xl text-[var(--ink-heading)] mt-3 leading-tight">
          Operating picture across the estate
        </h1>
        <p className="text-[var(--ink-secondary)] mt-4 max-w-3xl text-lg leading-relaxed">
          A federated view of synthetic requests, evidence boundaries and
          KATLAS receipts. No raw data has been moved or centralised.
        </p>
      </header>

      {err && <p className="text-red-700">{err}</p>}
      {!data && !err && <p className="text-[var(--ink-muted)]">Loading…</p>}

      {data && (
        <div className="space-y-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Requests" value={data.total_requests} />
            <Stat label="Evidence refs shared" value={data.total_evidence_refs_shared} />
            <Stat label="Evidence withheld" value={data.total_evidence_withheld} />
            <Stat label="Receipts issued" value={data.total_receipts_signed} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <BreakdownBlock title="By status" items={data.by_status} />
            <BreakdownBlock title="By risk level" items={data.by_risk} />
            <BreakdownBlock title="By case kind" items={data.by_kind} />
          </div>

          <div>
            <p className="eyebrow mb-4">All synthetic requests</p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="portfolio-list">
              {(data.items || []).map((r) => (
                <li key={r.id} className="border border-slate-200 bg-white p-5 hover:bg-[var(--bg-secondary)]/40 transition-colors">
                  <Link to={`/request/${r.id}`} className="block" data-testid={`portfolio-item-${r.id}`}>
                    <p className="font-mono-ui text-xs text-[var(--ink-mono)]">{r.id}</p>
                    <p className="font-display text-lg text-[var(--ink-heading)] mt-1">{r.title}</p>
                    <p className="text-xs text-[var(--ink-muted)] mt-2">{r.source_system} · {r.policy_id}</p>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </main>
  );
}
